import type { RefObject } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { aggridHeaderClass, SmallTitleSpan } from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { defaultSAL6030_R01GroupDto, SAL6030_R01GroupDto } from "./Dto_SAL6030_R01";
import { useSAL6030_R01Store } from "./Store_SAL6030_R01";

export const GROUP_COL_STATE_KEY = "SAL6030_R01-group-column-state";

interface Props {
	groupGridRef: RefObject<AgGridReact<SAL6030_R01GroupDto> | null>;
	widthPct: number;
}

export const TableGroup_SAL6030_R01 = ({ groupGridRef, widthPct }: Props) => {
	const {
		SAL6030_R01groupList,
		selectedSAL6030_R01Group,
		setSelectedSAL6030_R01Group,
		groupColumnState, setGroupColumnState,
		groupScrollTop,
		setGroupScrollTop,
	} = useSAL6030_R01Store();
	const [hoveredGroup, setHoveredGroup] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef: groupGridRef,
		key: "valueIndex",
		gridData: SAL6030_R01groupList,
		defaultGridData: defaultSAL6030_R01GroupDto,
		selectedRow: selectedSAL6030_R01Group,
	});

	const headerNames: Record<string, string> = {};

	const groupBaseColDefs: ColDef<SAL6030_R01GroupDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: "품목그룹코드", colId: "itemGroup", field: "itemGroup", width: 100,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: "품목그룹명", colId: "itemGroupNm", field: "itemGroupNm", width: 120,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: "도급자재비", colId: "contractCost", field: "contractCost", width: 110,
			type: "numericColumn",
			valueFormatter: (p: { value?: number }) => p.value?.toLocaleString() ?? "0",
		},
		{
			headerName: "SMT비용", colId: "smtCost", field: "smtCost", width: 100,
			type: "numericColumn",
			valueFormatter: (p: { value?: number }) => p.value?.toLocaleString() ?? "0",
		},
		{
			headerName: "원가합계", colId: "totalCost", field: "totalCost", width: 110,
			type: "numericColumn",
			valueFormatter: (p: { value?: number }) => p.value?.toLocaleString() ?? "0",
		},
	], []);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(groupBaseColDefs, [])
	, [groupBaseColDefs]);

	const groupColDefs: ColDef<SAL6030_R01GroupDto>[] = useMemo(() => {
		if (groupColumnState === null) return groupBaseColDefs;
		return GridCustomColumnDefs<SAL6030_R01GroupDto>({
			baseColumnDefs: groupBaseColDefs,
			columnState: groupColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [groupColumnState, settingRowIndex, groupBaseColDefs, headerNames]);

	const groupPinnedBottom = useMemo(
		() => [
			{
				itemGroup: SAL6030_R01groupList.length.toLocaleString() + "건",
				contractCost: SAL6030_R01groupList.reduce((s, i) => s + (i.contractCost || 0), 0).toLocaleString(),
				smtCost: SAL6030_R01groupList.reduce((s, i) => s + (i.smtCost || 0), 0).toLocaleString(),
				totalCost: SAL6030_R01groupList.reduce((s, i) => s + (i.totalCost || 0), 0).toLocaleString(),
			},
		],
		[SAL6030_R01groupList]
	);

	const handleGroupClick = useCallback(
		(e: RowClickedEvent<SAL6030_R01GroupDto>) => {
			if (e.node.rowPinned === "bottom") return;
			const row = e.data;
			if (!row) return;
			setSelectedSAL6030_R01Group(row);
		},
		[setSelectedSAL6030_R01Group]
	);

	useEffect(() => { ensureSelectedRow(); }, [SAL6030_R01groupList, selectedSAL6030_R01Group]);

	return (
		<div className="h-full flex flex-col" style={{ width: `${widthPct}%` }}>
			<SmallTitleSpan>품목그룹별 원가내역</SmallTitleSpan>
			<div className="flex-1 min-h-0">
				<GridContextWrapper<SAL6030_R01GroupDto>
					enableCellCalculation
					gridRef={groupGridRef}
					paddedGridData={paddedGridData}
					initSelected={() => setSelectedSAL6030_R01Group(undefined)}
					defaultRowData={defaultSAL6030_R01GroupDto}
					baseColumnDefs={groupColumnState ? groupColDefs : groupBaseColDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setGroupColumnState}
					localStorageKey={GROUP_COL_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Touch<SAL6030_R01GroupDto>
						ref={groupGridRef}
						rowData={paddedGridData}
						columnDefs={groupColDefs}
						defaultColDef={defaultColDef}
						getRowId={getRowId}
						rowClassRules={createRowClassRules<SAL6030_R01GroupDto>({
							idKey: "valueIndex",
							selectedList: selectedSAL6030_R01Group?.valueIndex ? [selectedSAL6030_R01Group.valueIndex] : [],
							deletedList: [],
						})}
						onRowClicked={handleGroupClick}
						onCellKeyDown={undefined}
						pinnedBottomRowData={groupPinnedBottom}
						setHoveredRowIndex={setHoveredGroup}
						columnStateKey={GROUP_COL_STATE_KEY}
						setColumnState={setGroupColumnState}
						scrollTop={groupScrollTop}
						setScrollTop={setGroupScrollTop}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
